import { Component, OnInit, OnDestroy, ViewEncapsulation, Input, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { concat, merge, of, from, Subscription, Subject } from 'rxjs';
import { map, concatMap, switchMap, finalize } from 'rxjs/operators';
import { FilterService } from 'primeng/api';
import { Table } from 'primeng/table';
import { saveAs } from 'file-saver';

import { environment } from 'src/environments/environment';

import { GlobalSettingsData } from 'src/app/shared/global-settings-data'
import { GetglobalsettingsService } from 'src/app/services/getglobalsettings.service'
import { RestApiService } from 'src/app/services/rest-api.service';

import { Currency, CurrencyRate, CurrencyRateListItem, EditCurrencyResult } from 'src/app/shared/currency';
import { currencySaveErrorText } from 'src/app/shared/currency-save-error';

/**
 * Currency and exchange-rate management table.
 *
 * On init the component reads global settings, registers five custom PrimeNG `FilterService`
 * strategies (`inArray`, `contain`, `before`, `gtOrZero`, `GteOrZero`), then calls
 * `loadClientInformation()` which fires two parallel HTTP calls:
 * - `getCurrency()` → `currencyList` (full currency list for the filter dropdown).
 * - `getCurrencyLatestRates()` → `currencyRatesList` (latest rate per currency, shown in the table).
 *
 * Add/edit operations open an inline `displayCurrencyDialog`; on confirm the dialog emits an
 * `EditCurrencyResult` to `onEditCurrency`, which saves it through `RestApiService.saveCurrencyEdit` (a new currency
 * with its first rate when `CURID` is null; otherwise a rename via `PUT /Currency/:id` and/or a new dated rate) and
 * reloads the table.
 *
 * Excel export is handled by `onExportData` using the `xlsx` and `file-saver` libraries.
 */
@Component({
  selector: 'app-list-currency-rates',
  templateUrl: './list-currency-rates.component.html',
  styleUrls: ['./list-currency-rates.component.css'],
  encapsulation: ViewEncapsulation.None
})
export class ListCurrencyRatesComponent implements OnInit, OnDestroy, OnChanges {

  globalSettinsData: GlobalSettingsData;

  /** Full currency list used to populate the filter dropdown. */
  currencyList: Currency[] = [];
  currencyListDS: Currency[] = [];

  /** Latest exchange rate rows displayed in the table. */
  currencyRatesList: CurrencyRateListItem[] = [];
  selectedCurrencyRatesItem: CurrencyRateListItem;

  private currencyQuery: Subscription;
  private currRateQuery: Subscription;

  loading: boolean = false;

  rows: number = 10;
  totalRecords: number = 0;

  dateFormat: string = 'dd.mm.yy';
  /** `true` when a row is selected in the table. */
  public get selection(): boolean { return this.selectedCurrencyRatesItem ? true : false; }

  /** Controls visibility of the add/edit currency dialog. */
  displayCurrencyDialog: boolean = false;
  /** The `Currency` object passed into the dialog for editing; `null` when creating a new one. */
  currencyToEdit: Currency = null;
  headerText: string = 'Редактирование валюты';
  editCurrencyRatesItem: CurrencyRateListItem = null;

  /** Failure text from the last save, shown inside the dialog (which stays open on failure). */
  saveError: string = '';

  private saveQuery: Subscription;
  private loadSeq: number = 0;

  /** True while a save request is in flight: the dialog is locked and the open handlers do nothing. */
  saving: boolean = false;

  /** CURID to select once the table reloads after a save (the new currency); else the current selection is kept. */
  private reselectCurId: number = null;

  @ViewChild("CRT1") public CRT1: Table;

  constructor(
    private restapi: RestApiService,
    private filterService: FilterService,
    private globalsettingsservice: GetglobalsettingsService,
  ) { }

  /** Unsubscribes all active HTTP subscriptions to prevent memory leaks. */
  ngOnDestroy(): void {
    console.log('*** ListCurrencyRatesComponent in ngOnDestroy')

    if (this.currencyQuery) { this.currencyQuery.unsubscribe(); }
    if (this.currRateQuery) { this.currRateQuery.unsubscribe(); }
    if (this.saveQuery) { this.saveQuery.unsubscribe(); }

  }

  /** Fires two parallel HTTP calls to load `currencyList` and `currencyRatesList`. */
  loadClientInformation() {
    if (this.globalSettinsData) {
      // Only the most recently started load may apply its result: an older one still in flight is cancelled and its
      // late response ignored, so it can neither overwrite fresher rows nor use up `reselectCurId` on a list that
      // does not contain the just-saved currency yet.
      const seq = ++this.loadSeq;
      if (this.currencyQuery) { this.currencyQuery.unsubscribe(); }
      if (this.currRateQuery) { this.currRateQuery.unsubscribe(); }

      this.loading = true;

      this.currencyQuery = this.restapi.getCurrency()
        .pipe(
          map(data => {
            if (seq === this.loadSeq) { this.currencyList = data || []; }
          })
        )
        .subscribe();

      this.currRateQuery = this.restapi.getCurrencyLatestRates()
          .pipe(
            map(data => {
              if (seq !== this.loadSeq) { return; }
              this.currencyRatesList = data || [];

              this.totalRecords = this.currencyRatesList.length;

              // The table's dataKey is CRID, which changes whenever a currency gets a new rate -- re-find the
              // selected row by CURID so the Edit button keeps working on the fresh row.
              const keepCurId = this.reselectCurId ?? this.selectedCurrencyRatesItem?.CURID;
              this.reselectCurId = null;
              this.selectedCurrencyRatesItem = (keepCurId != null) ? (this.currencyRatesList.find(r => r.CURID == keepCurId) ?? null) : null;
            }),

            finalize(() => {
              if (seq === this.loadSeq) { this.loading = false; }
            })
          )
          .subscribe();
    }
  }

  /** Reacts to `clientId` changes by reloading the currency and rate lists. */
  ngOnChanges(changes: SimpleChanges) {
    console.log('*** ListCurrencyRatesComponent ngOnChanges:');
    for (let propName in changes) {
      let change = changes[propName];

      switch (propName) {
        case 'clientId':
          this.loadClientInformation();
          break;
      }
    }
  }

  /** Reads global settings, registers custom PrimeNG filter strategies, then loads data. */
  ngOnInit(): void {
    console.log('*** ListCurrencyRatesComponent ngOnInit:');

    this.globalSettinsData = this.globalsettingsservice.getGlobalSettings();

    this.filterService.register('inArray', (value: any[], filter: any[]) => {

      return (filter.length > 0) && (filter.filter(e => e.CURCODE == value).length > 0);
    }
    );

    this.filterService.register('contain', (value: string, filter: string) => (
      ((filter != '') && (value.indexOf(filter) > -1))
    )
    );

    this.filterService.register('before', (value: Date, filter: string) => {

      return (filter != '') && (new Date(value) < new Date(filter));
    }
    );

    this.filterService.register('gtOrZero', (value: number, filter: number) => (
      ((filter == 0) && (value == 0)) || ((filter > 0) && (value > filter))
    )
    );

    this.filterService.register('GteOrZero', (value: number, filter: number) => (
      ((filter == 0) && (value == 0)) || ((filter > 0) && (value >= filter))
    )
    );
    this.loadClientInformation();
  }

  /** Handles table row selection event. */
  onRowSelect(event) {
    console.log('*** ListCurrencyRatesComponent onRowSelect: ' + JSON.stringify(event.data))
  }

  /** Handles table row deselection event. */
  onRowUnselect(event) {
    console.log('*** ListCurrencyRatesComponent onRowUnselect: ' + JSON.stringify(event.data))
  }

  /** Handles table sort event. */
  onSortCurrRates(event) {
    console.log('*** ListCurrencyRatesComponent onSortCurrRates: ' + JSON.stringify(event))
  }

  /** Handles table filter event. */
  onFilterTable(event) {
    console.log('*** ListCurrencyRatesComponent onFilterTable: ' + JSON.stringify(event))
  }

  /** Handles currency filter dropdown change. */
  onCurrListChange(event) {
    console.log('*** ListCurrencyRatesComponent onCurrListChange: ' + JSON.stringify(event));
  }

  /** Reloads both currency and rate lists (used after dialog save). */
  reloadTable(event) {
    console.log('*** ListCurrencyRatesComponent reloadTable: ' + JSON.stringify(event));

    this.loadClientInformation();
  }

  /** Opens the dialog in new-currency mode (`CURID` null: code and name are typed in, the rate is the first one). */
  onAddRow(event) {
    console.log('*** ListCurrencyRatesComponent in onAddRow: ' + JSON.stringify(event));
    if (this.saving) { return; }
    this.headerText = 'Создание новой валюты';
    this.saveError = '';
    this.currencyToEdit = null;
    this.editCurrencyRatesItem = { CRID: 0, CURID: null, CURCODE: '', CURNAME: '', CRDATE: new Date(), CRVALUE: 0 };
    this.displayCurrencyDialog = true;
  }

  /**
   * Opens the dialog for the selected currency: code and name editable (rename). The row's rate and date are passed
   * through unchanged as the persisted baseline; the dialog pre-fills that rate dated today. Only touching the rate or
   * date to a different (value, day) pair adds a new dated rate row -- it never overwrites the old one.
   */
  onEditRow(event) {
    console.log('*** ListCurrencyRatesComponent in onEditRow: ' + JSON.stringify(event));
    if (this.saving) { return; }
    if (!this.selectedCurrencyRatesItem) { return; }
    this.headerText = 'Редактирование валюты';
    this.saveError = '';
    this.currencyToEdit = null;
    this.editCurrencyRatesItem = { ...this.selectedCurrencyRatesItem, CRID: 0 };
    this.displayCurrencyDialog = true;
  }

  /** Remove-all handler — currently a no-op. */
  onRemoveAllRow(event) {
    console.log('*** ListCurrencyRatesComponent in onRemoveRow: ' + JSON.stringify(event));

  }

  /** Remove-row handler — currently a no-op. */
  onRemoveRow(event) {
    console.log('*** ListCurrencyRatesComponent in onRemoveRow: ' + JSON.stringify(event));
  }

  /**
   * Saves what the dialog confirmed through `RestApiService.saveCurrencyEdit`, then reloads the table. The dialog stays open on
   * failure and shows `saveError`; on success it closes and, if "select after closing" was ticked, the saved
   * currency becomes the selected row.
   */
  onEditCurrency(e: EditCurrencyResult) {
    if (!e || this.saving) { return; }
    this.saving = true;
    this.saveError = '';

    this.saveQuery = this.restapi.saveCurrencyEdit(e)
      .pipe(finalize(() => this.saving = false))
      .subscribe({
        next: created => {
          this.displayCurrencyDialog = false;
          if (e.selectAfter) { this.reselectCurId = created.CURID; }
          this.loadClientInformation();
        },
        error: (err: unknown) => { this.saveError = currencySaveErrorText(err); }
      });
  }

  /** Resets the PrimeNG table filters and reloads data. */
  clearOrResetTableFilter(event) {
    console.log('*** ListCurrencyRatesComponent clearOrResetTableFilter: ' + JSON.stringify(event));

    this.CRT1.reset();

    this.loadClientInformation();
  }

  /** Writes `buffer` to an `.xlsx` file using `file-saver`. */
  saveAsExcelFile(buffer: any, fileName: string): void {
    import("file-saver").then(FileSaver => {
      const EXCEL_TYPE = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
      const EXCEL_EXTENSION = ".xlsx";
      const data: Blob = new Blob([buffer], { type: EXCEL_TYPE });

      saveAs(data, fileName + "_export_" + new Date().getTime() + EXCEL_EXTENSION);
    });
  }

  /** Exports `currencyRatesList` to an Excel file via the `xlsx` library. */
  onExportData() {
    console.log('*** HomeRecentOrdersListComponent onExportData: ')

    import("xlsx").then(xlsx => {

      environment.production || console.table(this.currencyRatesList);

      const exportData: any =
        this.currencyRatesList.map(e => ({

          'Код': e.CURCODE,
          'Название': e.CURNAME,
          'Дата': new Date(e.CRDATE),
          'Курс': e.CRVALUE,
        }));

      const worksheet = xlsx.utils.json_to_sheet(exportData || []);
      const workbook = { Sheets: { rates: worksheet }, SheetNames: ['rates'] };

      const excelBuffer: any = xlsx.write(workbook, { bookType: "xlsx", type: "array" });

      this.saveAsExcelFile(excelBuffer, "rates");
    });
  }
}
